import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './generated';

let client: SupabaseClient<Database> | null = null;
let initOpts: InitSupabaseOptions | null = null;

export interface InitSupabaseOptions {
  url: string;
  anonKey: string;
  // For Edge Functions and CI only. NEVER pass this in browser code.
  serviceRoleKey?: string;
  // Override storage (used by isolation tests to keep multiple sessions distinct).
  storage?: Storage;
  storageKey?: string;
}

/**
 * Initializes the singleton Supabase client. Call this exactly once at app
 * startup before any service function is called.
 *
 * The whole monorepo enforces a single rule: **only this package may import
 * `@supabase/supabase-js`**. That keeps the client surface auditable and lets
 * us swap drivers, add tracing, or inject the active-context headers in one
 * place. See `eslint.config.js` for the enforcing rule.
 *
 * Idempotent: re-invocations with the same URL+key return the existing
 * singleton. This is required because Vite HMR re-evaluates the module that
 * calls `initSupabase` on every edit; without this guard, every save would
 * spin up a new GoTrue client against the same storageKey and the browser
 * console would spam `Multiple GoTrueClient instances detected ...`. If the
 * caller passes a different URL/key (e.g. swapping projects in dev), we
 * tear the old client down before building the new one.
 */
export function initSupabase(opts: InitSupabaseOptions): SupabaseClient<Database> {
  if (client && lastInitFingerprint === fingerprintOpts(opts)) {
    return client;
  }
  const key = opts.serviceRoleKey ?? opts.anonKey;
  client = createClient<Database>(opts.url, key, {
    auth: {
      autoRefreshToken: true,
      persistSession: opts.serviceRoleKey ? false : true,
      detectSessionInUrl: opts.serviceRoleKey ? false : true,
      storage: opts.storage,
      storageKey: opts.storageKey,
    },
    db: { schema: 'public' },
    global: {
      headers: { 'x-application-name': 'peptideclients' },
    },
  });
  initOpts = opts;
  lastInitFingerprint = fingerprintOpts(opts);
  return client;
}

/**
 * Returns the URL + anon key the singleton client was initialized with.
 *
 * Used by the small number of services that bypass the supabase-js client
 * (currently only `services/email.ts`, which talks to an Edge Function via a
 * plain fetch to keep the CORS surface stable across SDK upgrades). Throws
 * if `initSupabase` has not been called -- same contract as `supabase()`.
 */
export function supabaseEndpoint(): { url: string; anonKey: string } {
  if (!initOpts) {
    throw new Error(
      '[@pc/db] Supabase client not initialized. Call initSupabase({ url, anonKey }) at startup.',
    );
  }
  return { url: initOpts.url, anonKey: initOpts.anonKey };
}

/**
 * Returns the localStorage key prefix the singleton client persists its
 * session under, or `null` if no key was configured.
 *
 * Exposed so sign-out flows can manually wipe every localStorage entry
 * matching this prefix as a belt-and-suspenders measure. supabase-js's
 * own `signOut()` intentionally bails WITHOUT clearing storage when the
 * server-side `/auth/v1/logout` request returns a non-401/403/404 error
 * (network blip, 5xx, CORS misconfig). For a B2B SSO architecture that
 * trusts the central session, leaving a stale token in storage causes
 * "I clicked sign out and I'm still signed in" — see `services/auth.ts
 * signOut()` for the fallback wipe.
 */
export function currentStorageKey(): string | null {
  return initOpts?.storageKey ?? null;
}

let lastInitFingerprint: string | null = null;

function fingerprintOpts(opts: InitSupabaseOptions): string {
  // Cheap, deterministic, and never logs the key itself: storage key namespace
  // and url are enough to detect "different project / different session", and
  // we hash-prefix the secret material to detect rotation without exposing it.
  const keyPrefix = (opts.serviceRoleKey ?? opts.anonKey).slice(0, 12);
  return `${opts.url}|${opts.storageKey ?? ''}|${keyPrefix}`;
}

/**
 * Returns the live Supabase client. Throws if `initSupabase` has not been
 * called yet -- this is a programming error, not a runtime condition.
 */
export function supabase(): SupabaseClient<Database> {
  if (!client) {
    throw new Error(
      '[@pc/db] Supabase client not initialized. Call initSupabase({ url, anonKey }) at startup.',
    );
  }
  return client;
}

/**
 * For tests only: create a fresh client (does NOT replace the singleton).
 * Allows side-by-side sessions for the cross-org isolation test harness.
 */
export function createIsolatedClient(opts: InitSupabaseOptions): SupabaseClient<Database> {
  const key = opts.serviceRoleKey ?? opts.anonKey;
  return createClient<Database>(opts.url, key, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
      storageKey: opts.storageKey,
    },
    db: { schema: 'public' },
  });
}
